import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export const Footer = () => {
    const { t } = useLanguage();
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-primary text-primary-foreground">
            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Brand Column */}
                    <div className="lg:col-span-1">
                        <div className="flex items-center gap-3 mb-6">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/d7t95s72_Ads%C4%B1z%20tasar%C4%B1m.png"
                                alt="Girne MUN Logo"
                                className="h-16 w-auto"
                            />
                        </div>
                        <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6">
                            {t('footer.description')}
                        </p>
                        <div className="flex items-center gap-2 text-accent">
                            <span className="font-display font-semibold">27-28-29 March 2026</span>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-display font-semibold text-lg mb-6 text-primary-foreground">
                            {t('footer.quickLinks')}
                        </h4>
                        <ul className="space-y-3">
                            {[
                                { name: t('nav.home'), path: '/' },
                                { name: t('nav.about'), path: '/about' },
                                { name: t('nav.committees'), path: '/committees' },
                                { name: t('nav.venue'), path: '/venue' },
                                { name: t('nav.applyNow'), path: '/apply' },
                                { name: t('nav.contact'), path: '/contact' },
                            ].map((link) => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-primary-foreground/70 hover:text-accent transition-colors text-sm flex items-center gap-2"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-accent/50" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="font-display font-semibold text-lg mb-6 text-primary-foreground">
                            {t('footer.contactUs')}
                        </h4>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm text-primary-foreground/70">{t('footer.email')}</p>
                                    <a 
                                        href="mailto:girnemun26@gmail.com" 
                                        className="text-primary-foreground hover:text-accent transition-colors"
                                    >
                                        girnemun26@gmail.com
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm text-primary-foreground/70">{t('footer.phone')}</p>
                                    <a 
                                        href="tel:+905335101406" 
                                        className="text-primary-foreground hover:text-accent transition-colors"
                                    >
                                        +90 533 510 14 06
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm text-primary-foreground/70">{t('footer.venue')}</p>
                                    <p className="text-primary-foreground">
                                        Girne College Campus<br />
                                        Adana, Turkey
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Conference Info */}
                    <div>
                        <h4 className="font-display font-semibold text-lg mb-6 text-primary-foreground">
                            {t('footer.conference')}
                        </h4>
                        <div className="space-y-4">
                            <div className="p-4 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                                <p className="text-sm text-primary-foreground/70 mb-1">{t('footer.location')}</p>
                                <p className="font-semibold text-primary-foreground">Adana, Turkey</p>
                            </div>
                            <div className="p-4 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                                <p className="text-sm text-primary-foreground/70 mb-1">{t('footer.dates')}</p>
                                <p className="font-semibold text-primary-foreground">27-28-29 March 2026</p>
                            </div>
                            <Link 
                                to="/apply"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent-light transition-colors"
                            >
                                {t('footer.applyNow')}
                                <ExternalLink className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-primary-foreground/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-sm text-primary-foreground/60">
                            © {currentYear} Girne Model United Nations. {t('footer.allRights')}
                        </p>
                        <div className="flex items-center gap-6">
                            <Link 
                                to="/privacy" 
                                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                            >
                                {t('footer.privacyPolicy')}
                            </Link>
                            <Link 
                                to="/terms" 
                                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                            >
                                {t('footer.termsOfService')}
                            </Link>
                            <Link 
                                to="/admin/login" 
                                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                            >
                                {t('footer.admin')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
